/**
 * Authentication State Machine Hook
 * Manages authentication flow with proper state transitions and race condition prevention
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  initializeSession, 
  clearAuthToken,
  storeAuthToken,
  createJWT 
} from '../utils/session.js';
import { handleOAuthCallback } from '../utils/auth.js';
import { getUserData } from '../utils/roleMapping.js';
import { getRoleByName } from '../constants/roles.js';

// Authentication states
export const AUTH_STATES = {
  IDLE: 'idle',
  INITIALIZING: 'initializing',
  AUTHENTICATED: 'authenticated',
  AUTHENTICATING: 'authenticating',
  OAUTH_CALLBACK: 'oauth_callback',
  ERROR: 'error',
  EXPIRED: 'expired'
};

// Authentication actions
export const AUTH_ACTIONS = {
  INITIALIZE: 'initialize',
  LOGIN_START: 'login_start',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_ERROR: 'login_error',
  OAUTH_CALLBACK_START: 'oauth_callback_start',
  OAUTH_CALLBACK_SUCCESS: 'oauth_callback_success',
  OAUTH_CALLBACK_ERROR: 'oauth_callback_error',
  LOGOUT: 'logout',
  TOKEN_EXPIRED: 'token_expired',
  RETRY: 'retry',
  RESET_ERROR: 'reset_error'
};

// Error types
export const ERROR_TYPES = {
  NETWORK: 'network',
  OAUTH: 'oauth',
  AUTHORIZATION: 'authorization',
  SESSION: 'session',
  UNKNOWN: 'unknown'
};

// State machine transitions
const stateTransitions = {
  [AUTH_STATES.IDLE]: {
    [AUTH_ACTIONS.INITIALIZE]: AUTH_STATES.INITIALIZING,
    [AUTH_ACTIONS.LOGIN_START]: AUTH_STATES.AUTHENTICATING,
    [AUTH_ACTIONS.OAUTH_CALLBACK_START]: AUTH_STATES.OAUTH_CALLBACK
  },
  [AUTH_STATES.INITIALIZING]: {
    [AUTH_ACTIONS.LOGIN_SUCCESS]: AUTH_STATES.AUTHENTICATED,
    [AUTH_ACTIONS.LOGIN_ERROR]: AUTH_STATES.ERROR,
    [AUTH_ACTIONS.LOGOUT]: AUTH_STATES.IDLE,
    [AUTH_ACTIONS.OAUTH_CALLBACK_START]: AUTH_STATES.OAUTH_CALLBACK
  },
  [AUTH_STATES.AUTHENTICATING]: {
    [AUTH_ACTIONS.LOGIN_SUCCESS]: AUTH_STATES.AUTHENTICATED,
    [AUTH_ACTIONS.LOGIN_ERROR]: AUTH_STATES.ERROR,
    [AUTH_ACTIONS.LOGOUT]: AUTH_STATES.IDLE,
    [AUTH_ACTIONS.OAUTH_CALLBACK_START]: AUTH_STATES.OAUTH_CALLBACK
  },
  [AUTH_STATES.OAUTH_CALLBACK]: {
    [AUTH_ACTIONS.OAUTH_CALLBACK_SUCCESS]: AUTH_STATES.AUTHENTICATED,
    [AUTH_ACTIONS.OAUTH_CALLBACK_ERROR]: AUTH_STATES.ERROR,
    [AUTH_ACTIONS.LOGOUT]: AUTH_STATES.IDLE
  },
  [AUTH_STATES.AUTHENTICATED]: {
    [AUTH_ACTIONS.LOGOUT]: AUTH_STATES.IDLE,
    [AUTH_ACTIONS.TOKEN_EXPIRED]: AUTH_STATES.EXPIRED,
    [AUTH_ACTIONS.LOGIN_ERROR]: AUTH_STATES.ERROR
  },
  [AUTH_STATES.ERROR]: {
    [AUTH_ACTIONS.RETRY]: AUTH_STATES.AUTHENTICATING,
    [AUTH_ACTIONS.LOGOUT]: AUTH_STATES.IDLE,
    [AUTH_ACTIONS.RESET_ERROR]: AUTH_STATES.IDLE,
    [AUTH_ACTIONS.OAUTH_CALLBACK_START]: AUTH_STATES.OAUTH_CALLBACK
  },
  [AUTH_STATES.EXPIRED]: {
    [AUTH_ACTIONS.LOGIN_START]: AUTH_STATES.AUTHENTICATING,
    [AUTH_ACTIONS.LOGOUT]: AUTH_STATES.IDLE
  }
};

const useAuthStateMachine = (skipInitialization = false) => {
  const [state, setState] = useState(AUTH_STATES.IDLE);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Refs to prevent race conditions
  const stateRef = useRef(state);
  const operationRef = useRef(null);
  
  // Update state ref when state changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Transition function with validation
  const transition = useCallback((action, payload = null) => {
    const currentState = stateRef.current;
    const newState = stateTransitions[currentState]?.[action];
    
    if (!newState) {
      console.warn(`Invalid transition: ${action} from state ${currentState}`);
      return false;
    }
    
    console.log(`Auth state transition: ${currentState} -> ${newState} (${action})`);
    setState(newState);
    
    // Handle side effects based on new state
    switch (newState) {
      case AUTH_STATES.ERROR:
        if (payload?.error) {
          setError(payload.error);
        }
        break;
      case AUTH_STATES.AUTHENTICATED:
        if (payload) {
          setUser(payload.user);
          setRole(payload.role);
          setPermissions(payload.permissions);
          setError(null);
          setRetryCount(0);
        }
        break;
      case AUTH_STATES.IDLE:
        setUser(null);
        setRole(null);
        setPermissions([]);
        setError(null);
        setRetryCount(0);
        break;
      default:
        // No side effects for other states
        break;
    }
    
    return true;
  }, []);

  // Async operation wrapper to prevent race conditions
  const executeOperation = useCallback(async (operationId, operation) => {
    // Cancel previous operation if still running
    if (operationRef.current) {
      console.log('Cancelling previous auth operation');
      operationRef.current.cancelled = true;
    }
    
    // Create new operation context
    const operationContext = { cancelled: false, id: operationId };
    operationRef.current = operationContext;
    
    try {
      const result = await operation();
      
      // Check if operation was cancelled
      if (operationContext.cancelled) {
        console.log(`Auth operation ${operationId} was cancelled`);
        return null;
      }
      
      return result;
    } finally {
      // Clear operation ref if this was the current operation
      if (operationRef.current === operationContext) {
        operationRef.current = null;
      }
    }
  }, []);

  // Create error object with type detection
  const createError = useCallback((error, type = ERROR_TYPES.UNKNOWN) => {
    let errorType = type;
    let userMessage = 'An unexpected error occurred';
    let retryable = true;

    if (error?.message) {
      const message = error.message.toLowerCase();
      
      if (message.includes('network') || message.includes('fetch')) {
        errorType = ERROR_TYPES.NETWORK;
        userMessage = 'Network connection problem. Please check your internet connection.';
      } else if (message.includes('oauth') || message.includes('state') || message.includes('token')) {
        errorType = ERROR_TYPES.OAUTH;
        userMessage = 'Authentication failed. Please try signing in again.';
      } else if (message.includes('access denied') || message.includes('unauthorized') || message.includes('not authorized')) {
        errorType = ERROR_TYPES.AUTHORIZATION;
        userMessage = 'Access denied. Please contact an administrator.';
        retryable = false;
      } else if (message.includes('session') || message.includes('expired')) {
        errorType = ERROR_TYPES.SESSION;
        userMessage = 'Your session has expired. Please sign in again.';
      }
    }

    return {
      type: errorType,
      message: error?.message || 'Unknown error',
      userMessage,
      retryable,
      timestamp: Date.now()
    };
  }, []);

  // Initialize authentication state
  const initialize = useCallback(async () => {
    if (stateRef.current !== AUTH_STATES.IDLE) {
      return;
    }

    transition(AUTH_ACTIONS.INITIALIZE);

    try {
      const result = await executeOperation('initialize', async () => {
        const session = initializeSession();
        
        if (session && session.user) {
          try {
            // Refresh user role from database with retry logic
            const userData = await getUserData(session.user.email);
            const roleData = getRoleByName(userData.role);
            
            // Store updated session for persistence
            const updatedPayload = {
              sub: session.user.id,
              email: session.user.email,
              name: session.user.name,
              picture: session.user.picture,
              role: userData.role,
              permissions: roleData.permissions
            };
            
            const newToken = createJWT(updatedPayload);
            storeAuthToken(newToken);
            
            return {
              user: {
                ...session.user,
                role: userData.role,
                displayRole: userData.displayRole
              },
              role: userData.role,
              permissions: roleData.permissions
            };
          } catch (roleError) {
            console.warn('Failed to refresh role, using session data:', roleError);
            
            // Only proceed if session is still valid
            if (session.expiresAt && Date.now() < session.expiresAt) {
              return {
                user: session.user,
                role: session.role,
                permissions: session.permissions
              };
            } else {
              throw new Error('Session expired');
            }
          }
        }
        
        return null;
      });

      if (result) {
        transition(AUTH_ACTIONS.LOGIN_SUCCESS, result);
      } else {
        transition(AUTH_ACTIONS.LOGOUT);
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      const errorObj = createError(error, ERROR_TYPES.SESSION);
      transition(AUTH_ACTIONS.LOGIN_ERROR, { error: errorObj });
    }
  }, [transition, executeOperation, createError]);

  // Handle OAuth callback
  const handleCallback = useCallback(async (urlFragment, state) => {
    // Already authenticated — just return success
    if (stateRef.current === AUTH_STATES.AUTHENTICATED) {
      return true;
    }

    // If already processing, wait for it to finish instead of hanging
    if (stateRef.current === AUTH_STATES.OAUTH_CALLBACK) {
      console.log('OAuth callback already in progress, waiting...');
      return new Promise((resolve) => {
        const checkDone = setInterval(() => {
          const s = stateRef.current;
          if (s === AUTH_STATES.AUTHENTICATED) { clearInterval(checkDone); resolve(true); }
          else if (s === AUTH_STATES.ERROR || s === AUTH_STATES.IDLE) { clearInterval(checkDone); resolve(false); }
        }, 100);
        setTimeout(() => { clearInterval(checkDone); resolve(false); }, 10000);
      });
    }

    transition(AUTH_ACTIONS.OAUTH_CALLBACK_START);

    try {
      const result = await executeOperation('oauth_callback', async () => {
        const authData = await handleOAuthCallback(urlFragment, state);
        
        // Resolve role: try DB lookup but fall back to domain-based auth immediately
        // This prevents a hanging/403 proxy call from blocking login for workspace users
        let userData;
        try {
          userData = await Promise.race([
            getUserData(authData.user.email),
            new Promise((_, reject) => setTimeout(() => reject(new Error('role_lookup_timeout')), 8000))
          ]);
        } catch (roleErr) {
          console.warn('Role lookup failed/timed out, using domain fallback:', roleErr.message);
          // Domain-based fallback — mirror logic in roleMapping.js getUserRole
          const authorizedDomains = ['satellite-talent.com', 'talantascout.com'];
          const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '').split(',').map(e => e.trim()).filter(Boolean);
          const userDomain = authData.user.email.split('@')[1];
          if (!authorizedDomains.includes(userDomain)) {
            throw new Error('Access denied: User not authorized. Please contact an administrator.');
          }
          const isAdmin = adminEmails.includes(authData.user.email);
          const roleName = isAdmin ? 'admin' : 'talent-partner';
          userData = {
            email: authData.user.email,
            role: roleName,
            displayRole: isAdmin ? 'Administrator' : 'Talent Partner',
            permissions: isAdmin
              ? ['view_all_nps', 'view_individual_client_nps', 'manage_users', 'view_audit_logs', 'export_all_data', 'manage_client_access']
              : ['view_candidates', 'view_aggregated_nps', 'export_filtered_data'],
            isActive: true
          };
        }
        const roleData = getRoleByName(userData.role);

        // Create and store JWT
        const jwtPayload = {
          sub: authData.user.id,
          email: authData.user.email,
          name: authData.user.name,
          picture: authData.user.picture,
          role: userData.role,
          permissions: roleData.permissions,
          tokens: authData.tokens
        };

        const jwt = createJWT(jwtPayload);
        const stored = storeAuthToken(jwt);
        
        if (!stored) {
          throw new Error('Failed to store authentication token');
        }

        return {
          user: {
            ...authData.user,
            role: userData.role,
            displayRole: userData.displayRole
          },
          role: userData.role,
          permissions: roleData.permissions
        };
      });

      if (result) {
        transition(AUTH_ACTIONS.OAUTH_CALLBACK_SUCCESS, result);
        return true;
      }
    } catch (error) {
      console.error('OAuth callback failed:', error);
      const errorObj = createError(error, ERROR_TYPES.OAUTH);
      transition(AUTH_ACTIONS.OAUTH_CALLBACK_ERROR, { error: errorObj });
    }

    return false;
  }, [transition, executeOperation, createError]);

  // Login with auth data
  const login = useCallback(async (authData) => {
    transition(AUTH_ACTIONS.LOGIN_START);

    try {
      const result = await executeOperation('login', async () => {
        const userData = await getUserData(authData.user.email);
        const roleData = getRoleByName(userData.role);

        const jwtPayload = {
          sub: authData.user.id,
          email: authData.user.email,
          name: authData.user.name,
          picture: authData.user.picture,
          role: userData.role,
          permissions: roleData.permissions,
          tokens: authData.tokens
        };

        const jwt = createJWT(jwtPayload);
        const stored = storeAuthToken(jwt);
        
        if (!stored) {
          throw new Error('Failed to store authentication token');
        }

        return {
          user: {
            ...authData.user,
            role: userData.role,
            displayRole: userData.displayRole
          },
          role: userData.role,
          permissions: roleData.permissions
        };
      });

      if (result) {
        transition(AUTH_ACTIONS.LOGIN_SUCCESS, result);
        return true;
      }
    } catch (error) {
      console.error('Login failed:', error);
      const errorObj = createError(error);
      transition(AUTH_ACTIONS.LOGIN_ERROR, { error: errorObj });
    }

    return false;
  }, [transition, executeOperation, createError]);

  // Logout
  const logout = useCallback(() => {
    // Cancel any ongoing operations
    if (operationRef.current) {
      operationRef.current.cancelled = true;
    }
    
    clearAuthToken();
    transition(AUTH_ACTIONS.LOGOUT);
  }, [transition]);

  // Retry failed operation
  const retry = useCallback(() => {
    if (retryCount >= 3) {
      return false;
    }
    
    setRetryCount(prev => prev + 1);
    transition(AUTH_ACTIONS.RETRY);
    return true;
  }, [retryCount, transition]);

  // Reset error state
  const resetError = useCallback(() => {
    transition(AUTH_ACTIONS.RESET_ERROR);
  }, [transition]);

  // Computed state values
  const isLoading = [
    AUTH_STATES.INITIALIZING, 
    AUTH_STATES.AUTHENTICATING, 
    AUTH_STATES.OAUTH_CALLBACK
  ].includes(state);

  const isAuthenticated = state === AUTH_STATES.AUTHENTICATED;
  const hasError = state === AUTH_STATES.ERROR;
  const isExpired = state === AUTH_STATES.EXPIRED;

  // Initialize on mount and handle page visibility changes
  useEffect(() => {
    // Skip initialization for public routes like login page
    if (!skipInitialization) {
      initialize();
    }
    
    // Re-validate session when page becomes visible (handles tab switching)
    const handleVisibilityChange = () => {
      if (!document.hidden && state === AUTH_STATES.AUTHENTICATED) {
        const session = initializeSession();
        if (!session) {
          // Session expired while tab was inactive
          transition(AUTH_ACTIONS.TOKEN_EXPIRED);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Auto-logout on session expiry
    const checkInterval = setInterval(() => {
      if (state === AUTH_STATES.AUTHENTICATED) {
        const session = initializeSession();
        if (!session) {
          transition(AUTH_ACTIONS.TOKEN_EXPIRED);
        }
      }
    }, 60000); // Check every minute
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(checkInterval);
    };
  }, [initialize, state, transition, skipInitialization]);

  return {
    // State
    state,
    user,
    role,
    permissions,
    error,
    retryCount,
    
    // Computed values
    isLoading: skipInitialization ? false : isLoading, // Don't show loading on public routes
    isAuthenticated,
    hasError,
    isExpired,
    
    // Actions
    login,
    logout,
    handleCallback,
    retry,
    resetError,
    initialize,
    
    // Utils
    canRetry: hasError && error?.retryable && retryCount < 3
  };
};

export default useAuthStateMachine;